import React, { useState, useEffect } from 'react';
import { ArrowLeft } from "lucide-react";
import ErrorModal from '../ui/ErrorModal';
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { API_BASE_URL } from "../../firebase/firebaseConfig";
import { ReactComponent as HigloLogo } from '../../assets/signin-visuals/higlo_logo.svg';

function SignIn({ onEmailSubmit }) {
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [emailDisabled, setEmailDisabled] = useState(false);
  const [isHandlingSubmit, setIsHandlingSubmit] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Pre-populate email from URL query parameter if present
    const urlParams = new URLSearchParams(window.location.search);
    const emailParam = urlParams.get('email');
    if (emailParam) {
      setEmail(emailParam);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsHandlingSubmit(true);

    // Send email to backend
    try {
      const response = await fetch(
        `${API_BASE_URL}/user/start`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        }
      );

      const data = await response.json();

      if (data.allowed) {
        // Proceed to code verification
        onEmailSubmit(email);
      } else {
        // Show message and disable email input
        setErrorMessage('You have not purchased or been invited to manage any Higlo devices, or your account has not been activated yet. Please contact support at hq@higlo.co with any questions.');
        setEmailDisabled(true);
      }
    } catch (error) {
      console.error('Error:', error);
      setErrorMessage('An error occurred. Please try again.');
      setShowErrorModal(true);
    } finally {
      setIsHandlingSubmit(false);
    }
  };

  const handleGoBack = () => {
    setEmailDisabled(false);
    setErrorMessage('');
    setShowErrorModal(false);
  };

  const closeErrorModal = () => {
    setShowErrorModal(false);
    setErrorMessage('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-md mx-auto space-y-8 pt-12">
        {/* Logo */}
        <div className="flex justify-center">
          <HigloLogo className="w-10 h-10" />
        </div>

        {/* Header */}
        <div>
          <h1 className="text-3xl font-title font-normal text-slate-900 text-center">Higlo Account Portal</h1>
          <p className="text-slate-600 text-center mt-3">
          Sign in to confirm shipping details, or if you already have your devices, to configure and manage them.
          </p>
        </div>

        {/* Sign In Card */}
        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-slate-700">
                  Email address
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@domain.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={emailDisabled}
                  required
                  className="pl-10"
                />
              </div>

              {!emailDisabled && (
                <Button
                  type="submit"
                  variant="default"
                  disabled={isHandlingSubmit}
                  className="w-full bg-gray-800 hover:bg-gray-700"
                >
                  {isHandlingSubmit ? "Signing in..." : "Continue"}
                </Button>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Error Message */}
        {errorMessage && emailDisabled && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <p className="text-red-800 font-medium mb-4">{errorMessage}</p>
              <Button
                variant="outline"
                onClick={handleGoBack}
                className="w-full flex items-center justify-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Try Again
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Error Modal */}
        <ErrorModal
          isOpen={showErrorModal}
          message={errorMessage}
          onClose={closeErrorModal}
        />
      </div>
    </div>
  );
}

export default SignIn;
